// frontend/src/pages/accounting/ManualJournalEntryForm.jsx
import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import axios from "axios";
import {
  FiX, FiPlus, FiTrash2, FiSave, FiSend, FiAlertCircle,
  FiCheckCircle, FiClock, FiFileText, FiRefreshCw, FiSearch
} from "react-icons/fi";
import { useAuth } from "../../contexts/AuthContext";
import { useCurrency } from "../../pages/CurrencyContext";
import "./ManualJournalEntryForm.css";

// ✅ Backend base URL — same pattern as Banktype.jsx
const url = process.env.REACT_APP_BACKEND_URL;

// ✅ Helper: build auth header from localStorage token
const getAuthHeaders = () => {
  const token = localStorage.getItem("token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

// ---------- Helpers ----------
const generateLineId = () => `${Date.now()}-${Math.random().toString(36).slice(2)}`;

const initialLine = () => ({
  id: generateLineId(),
  account_id: "",
  description: "",
  debit: "",
  credit: "",
  customer_id: "",
  supplier_id: "",
});

const createEmptyForm = (referenceNo = "", currencyCode = "USD") => ({
  _id: null,
  version: 1,
  transaction_no: referenceNo,
  reference_no: referenceNo,
  journal_date: new Date().toISOString().slice(0, 10),
  description: "",
  currency_code: currencyCode,
  status: "draft",
  lines: [initialLine(), initialLine()],
});

// ✅ Robust helper to extract a 3‑letter currency code from company object
const getCompanyCurrency = (company) => {
  if (!company) return "USD";
  let currency = null;

  if (company.currency) {
    if (typeof company.currency === "object" && company.currency.code) {
      currency = company.currency.code;
    } else if (typeof company.currency === "string" && /^[A-Z]{3}$/.test(company.currency)) {
      currency = company.currency;
    }
  }

  if (!currency && company.currency_code) {
    if (typeof company.currency_code === "string" && /^[A-Z]{3}$/.test(company.currency_code)) {
      currency = company.currency_code;
    } else if (typeof company.currency_code === "object" && company.currency_code.code) {
      currency = company.currency_code.code;
    }
  }

  if (!currency && company.base_currency) {
    if (typeof company.base_currency === "string" && /^[A-Z]{3}$/.test(company.base_currency)) {
      currency = company.base_currency;
    } else if (typeof company.base_currency === "object" && company.base_currency.code) {
      currency = company.base_currency.code;
    }
  }

  if (typeof currency !== "string" || !/^[A-Z]{3}$/.test(currency)) {
    currency = "USD";
  }

  return currency;
};

const unwrapError = (error, fallback) => {
  const message = error?.response?.data?.message || error?.message || fallback;
  const details = error?.response?.data?.details;
  if (Array.isArray(details) && details.length) {
    return `${message}: ${details.join(", ")}`;
  }
  return message;
};

const statusLabel = (status) =>
  status ? status.charAt(0).toUpperCase() + status.slice(1) : "Draft";

// ---------- Account Dropdown ----------
const AccountDropdown = ({
  accountsTree,
  value,
  onChange,
  selectedAccounts = [],
  disabled = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState("");
  const dropdownRef = useRef(null);

  const flattenTree = (nodes, prefix = "") => {
    let result = [];
    for (const node of nodes) {
      const label = `${prefix}${node.account_code} — ${node.account_name}`;
      const selectable = node.allow_manual_posting && !node.is_control_account;
      result.push({ ...node, label, depth: prefix.length / 2, selectable });
      if (node.children) {
        result = result.concat(flattenTree(node.children, prefix + "  "));
      }
    }
    return result;
  };

  const flatAccounts = flattenTree(accountsTree);
  const filtered = flatAccounts.filter(acc => {
    const matchesSearch = acc.label.toLowerCase().includes(search.toLowerCase());
    const isNotSelectedElsewhere = acc._id === value || !selectedAccounts.includes(acc._id);
    return matchesSearch && isNotSelectedElsewhere;
  });
  const selected = flatAccounts.find(acc => acc._id === value);

  const handleSelect = (account) => {
    if (!account.selectable) {
      toast.warning("Select a leaf posting account (not a parent/control account)");
      return;
    }
    onChange(account._id);
    setIsOpen(false);
    setSearch("");
  };

  useEffect(() => {
    const handleOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleOutside);
    return () => document.removeEventListener("mousedown", handleOutside);
  }, []);

  return (
    <div className="mj-account-dropdown" ref={dropdownRef}>
      <div className="mj-dropdown-header" onClick={() => !disabled && setIsOpen(!isOpen)}>
        <span>{selected ? selected.label : "-- Select Account --"}</span>
        <span className="mj-dropdown-arrow">▾</span>
      </div>
      {isOpen && !disabled && (
        <div className="mj-dropdown-menu">
          <input
            type="text"
            placeholder="Search account..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="mj-dropdown-search"
            autoFocus
          />
          <div className="mj-dropdown-list">
            {filtered.length === 0 && <div className="mj-dropdown-empty">No accounts found</div>}
            {filtered.map(acc => (
              <div
                key={acc._id}
                className={`mj-dropdown-item ${!acc.selectable ? "mj-disabled" : ""}`}
                onClick={() => handleSelect(acc)}
                style={{ paddingLeft: `${acc.depth * 16 + 8}px` }}
              >
                <span>{acc.label}</span>
                {!acc.selectable && <span className="mj-badge">Parent</span>}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// ---------- Main Component ----------
const ManualJournalEntryForm = () => {
  const { company, userType, getCompanyId } = useAuth();
  const { formatCurrency, getCurrencySymbol } = useCurrency();
  const navigate = useNavigate();

  // ✅ Get company currency safely
  const companyCurrency = useMemo(() => getCompanyCurrency(company), [company]);

  // ✅ Format amount with company currency
  const formatAmount = (amount) => {
    if (amount === undefined || amount === null) return formatCurrency(0, companyCurrency);
    return formatCurrency(amount, companyCurrency);
  };

  const companyId = getCompanyId?.() || company?._id || company?.id || "";
  const companyParams = useMemo(
    () => (userType === "system" && companyId ? { company_id: companyId } : {}),
    [companyId, userType]
  );

  // ---------- State ----------
  const [accountsTree, setAccountsTree] = useState([]);
  const [parties, setParties] = useState({ customers: [], suppliers: [] });
  const [ownerAccounts, setOwnerAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);

  const [form, setForm] = useState(createEmptyForm("", companyCurrency));
  const [partyType, setPartyType] = useState("");
  const [partyId, setPartyId] = useState("");

  const [entries, setEntries] = useState([]);
  const [summary, setSummary] = useState({
    total: 0,
    draft: 0,
    posted: 0,
    reversed: 0,
  });
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  // ---------- Derived ----------
  const totals = useMemo(
    () =>
      form.lines.reduce(
        (result, line) => ({
          debit: result.debit + Number(line.debit || 0),
          credit: result.credit + Number(line.credit || 0),
        }),
        { debit: 0, credit: 0 }
      ),
    [form.lines]
  );

  const difference = Math.round((totals.debit - totals.credit) * 100) / 100;
  const isBalanced = totals.debit > 0 && Math.abs(difference) < 0.005;
  const isDraft = form.status === "draft";
  const isPosted = form.status === "posted";

  // ---------- API Calls ----------
  const showMessage = useCallback((type, text) => {
    setMessage({ type, text });
    window.setTimeout(() => setMessage(null), 6500);
  }, []);

  const loadList = useCallback(
    async ({ searchValue = "", statusValue = "all" } = {}) => {
      const response = await axios.get(`${url}/api/accounting/manual-journal`, {
        params: {
          ...companyParams,
          search: searchValue.trim() || undefined,
          status: statusValue,
          limit: 100,
        },
        headers: getAuthHeaders(),
      });
      setEntries(response.data?.data || []);
      setSummary(
        response.data?.summary || {
          total: 0,
          draft: 0,
          posted: 0,
          reversed: 0,
        }
      );
    },
    [companyParams]
  );

  const refreshList = useCallback(
    () => loadList({ searchValue: search, statusValue: statusFilter }),
    [loadList, search, statusFilter]
  );

  const loadMetadata = useCallback(async () => {
    const [accountsRes, partiesRes] = await Promise.all([
      axios.get(`${url}/api/accounting/manual-journal/accounts`, {
        params: companyParams,
        headers: getAuthHeaders(),
      }),
      axios.get(`${url}/api/accounting/manual-journal/parties`, {
        params: companyParams,
        headers: getAuthHeaders(),
      }),
    ]);
    setAccountsTree(accountsRes.data.data || []);
    setParties(partiesRes.data.data || { customers: [], suppliers: [] });

    const extractOwnerAccounts = (nodes) => {
      for (const node of nodes) {
        if (node.system_account_role === "OWNER_CAPITAL") {
          if (node.children && node.children.length) {
            return node.children.map(child => ({
              _id: child._id,
              account_code: child.account_code,
              account_name: child.account_name,
              label: `${child.account_code} — ${child.account_name}`,
            }));
          }
          return [];
        }
        if (node.children) {
          const result = extractOwnerAccounts(node.children);
          if (result.length) return result;
        }
      }
      return [];
    };
    const owners = extractOwnerAccounts(accountsRes.data.data || []);
    setOwnerAccounts(owners);
  }, [companyParams]);

  const initialize = useCallback(async () => {
    setLoading(true);
    try {
      await Promise.all([loadMetadata(), loadList()]);
      setForm(createEmptyForm("", companyCurrency));
    } catch (error) {
      showMessage("error", unwrapError(error, "Failed to load data"));
    } finally {
      setLoading(false);
    }
  }, [loadMetadata, loadList, showMessage, companyCurrency]);

  useEffect(() => {
    initialize();
  }, [initialize]);

  useEffect(() => {
    if (loading) return;
    const timer = window.setTimeout(() => {
      refreshList().catch((error) =>
        showMessage("error", unwrapError(error, "Failed to refresh list"))
      );
    }, 250);
    return () => window.clearTimeout(timer);
  }, [refreshList, loading, showMessage]);

  // ---------- Party auto-fill ----------
  useEffect(() => {
    if (!partyType || !partyId) return;

    const hasEmptyLine = form.lines.some(line => !line.account_id);
    if (!hasEmptyLine) return;

    let accountId = null;

    if (partyType === "customer" || partyType === "supplier") {
      const findSubaccount = (node) => {
        if (node.system_account_role === (partyType === "customer" ? "CUSTOMER" : "SUPPLIER") &&
            node[`${partyType}_id`] && node[`${partyType}_id`].toString() === partyId.toString()) {
          return node._id;
        }
        if (node.children) {
          for (const child of node.children) {
            const result = findSubaccount(child);
            if (result) return result;
          }
        }
        return null;
      };

      let found = null;
      for (const root of accountsTree) {
        const result = findSubaccount(root);
        if (result) { found = result; break; }
      }
      accountId = found;
    } else if (partyType === "owner") {
      const owner = ownerAccounts.find(acc => acc._id === partyId);
      accountId = owner ? owner._id : null;
    }

    if (accountId) {
      const alreadyUsed = form.lines.some(line => line.account_id === accountId);
      if (alreadyUsed) {
        toast.warning(`Account is already used in another line.`);
        return;
      }

      setForm(prev => {
        const emptyIdx = prev.lines.findIndex(line => !line.account_id);
        if (emptyIdx === -1) return prev;
        const newLines = [...prev.lines];
        newLines[emptyIdx] = { ...newLines[emptyIdx], account_id: accountId };
        return { ...prev, lines: newLines };
      });
    } else {
      if (partyType !== "owner") {
        toast.warning(`No ${partyType} subaccount found. Please select manually.`);
      }
    }
  }, [partyType, partyId, accountsTree, ownerAccounts, form.lines]);

  // ---------- Form Handlers ----------
  const changeHeader = (field, value) => {
    setForm((current) => ({ ...current, [field]: value }));
  };

  const changeLine = (id, field, value) => {
    setForm((current) => ({
      ...current,
      lines: current.lines.map((line) => {
        if (line.id !== id) return line;
        if (field === "account_id") {
          return { ...line, account_id: value };
        }
        if (field === "debit") {
          const num = value === "" ? "" : Number(value);
          if (num !== "" && (isNaN(num) || num < 0)) return line;
          return {
            ...line,
            debit: value,
            credit: value !== "" && Number(value) > 0 ? "" : line.credit,
          };
        }
        if (field === "credit") {
          const num = value === "" ? "" : Number(value);
          if (num !== "" && (isNaN(num) || num < 0)) return line;
          return {
            ...line,
            credit: value,
            debit: value !== "" && Number(value) > 0 ? "" : line.debit,
          };
        }
        return { ...line, [field]: value };
      }),
    }));
  };

  const addLine = () => {
    setForm((current) => ({ ...current, lines: [...current.lines, initialLine()] }));
  };

  const removeLine = (id) => {
    setForm((current) => {
      const remaining = current.lines.filter((line) => line.id !== id);
      return {
        ...current,
        lines: remaining.length >= 2 ? remaining : [...remaining, initialLine()],
      };
    });
  };

  // ---------- Build Payload ----------
  const buildPayload = () => ({
    ...companyParams,
    journal_date: form.journal_date,
    reference_no: form.reference_no,
    transaction_party: partyType || undefined,
    customer_id: partyType === "customer" ? partyId : undefined,
    supplier_id: partyType === "supplier" ? partyId : undefined,
    owner_id: partyType === "owner" ? partyId : undefined,
    description: form.description,
    currency_code: form.currency_code,
    version: form.version,
    lines: form.lines
      .filter(line => line.account_id)
      .map(line => ({
        account_id: line.account_id,
        debit: Number(line.debit || 0),
        credit: Number(line.credit || 0),
        description: line.description || "",
      })),
  });

  // ---------- Save Draft ----------
  const saveDraft = async () => {
    if (!form.journal_date) {
      showMessage("error", "Journal date is required");
      return;
    }
    if (!form.description.trim()) {
      showMessage("error", "Description is required");
      return;
    }
    const hasEmptyLine = form.lines.some(line => !line.account_id);
    if (hasEmptyLine) {
      showMessage("error", "All lines must have an account selected");
      return;
    }

    setSaving(true);
    try {
      const payload = buildPayload();
      const response = form._id
        ? await axios.put(
            `${url}/api/accounting/manual-journal/${form._id}`,
            payload,
            { headers: getAuthHeaders() }
          )
        : await axios.post(
            `${url}/api/accounting/manual-journal`,
            payload,
            { headers: getAuthHeaders() }
          );

      const transaction = response.data?.data;
      if (transaction) {
        setForm({
          _id: transaction._id,
          version: transaction.version || 1,
          transaction_no: transaction.transaction_no || "",
          reference_no: transaction.reference_no || "",
          journal_date: String(transaction.transaction_date || new Date()).slice(0, 10),
          description: transaction.description || "",
          currency_code: transaction.currency_code || companyCurrency,
          status: transaction.status || "draft",
          lines: (transaction.lines || []).map(line => ({
            id: line._id || generateLineId(),
            account_id: typeof line.account_id === "object" ? line.account_id?._id || "" : line.account_id || "",
            customer_id: line.customer_id || "",
            supplier_id: line.supplier_id || "",
            debit: Number(line.debit || 0) > 0 ? String(line.debit) : "",
            credit: Number(line.credit || 0) > 0 ? String(line.credit) : "",
            description: line.description || "",
          })),
        });
        await refreshList();
        showMessage("success", response.data?.message || "Draft saved");
      }
    } catch (error) {
      showMessage("error", unwrapError(error, "Failed to save draft"));
    } finally {
      setSaving(false);
    }
  };

  // ---------- Post Journal ----------
  const postJournal = async () => {
    if (!isBalanced) {
      showMessage("error", "Total debit must equal total credit before posting");
      return;
    }

    if (isDraft) {
      await saveDraft();
      if (form.status !== "draft") {
        showMessage("error", "Unable to save draft before posting");
        return;
      }
    }

    if (!form._id) {
      showMessage("error", "Please save the draft first");
      return;
    }

    if (!window.confirm("Post this journal entry? Posted entries cannot be edited.")) {
      return;
    }

    setSaving(true);
    try {
      const response = await axios.post(
        `${url}/api/accounting/manual-journal/${form._id}/post`,
        companyParams,
        { headers: getAuthHeaders() }
      );
      const transaction = response.data?.data;
      if (transaction) {
        setForm({
          _id: transaction._id,
          version: transaction.version || 1,
          transaction_no: transaction.transaction_no || "",
          reference_no: transaction.reference_no || "",
          journal_date: String(transaction.transaction_date || new Date()).slice(0, 10),
          description: transaction.description || "",
          currency_code: transaction.currency_code || companyCurrency,
          status: transaction.status || "posted",
          lines: (transaction.lines || []).map(line => ({
            id: line._id || generateLineId(),
            account_id: typeof line.account_id === "object" ? line.account_id?._id || "" : line.account_id || "",
            customer_id: line.customer_id || "",
            supplier_id: line.supplier_id || "",
            debit: Number(line.debit || 0) > 0 ? String(line.debit) : "",
            credit: Number(line.credit || 0) > 0 ? String(line.credit) : "",
            description: line.description || "",
          })),
        });
        await refreshList();
        showMessage("success", response.data?.message || "Journal posted");
        if (response.data?.warning) {
          toast.warning(response.data.warning.message);
        }
      }
    } catch (error) {
      showMessage("error", unwrapError(error, "Failed to post journal"));
    } finally {
      setSaving(false);
    }
  };

  // ---------- Delete Draft ----------
  const deleteDraft = async () => {
    if (!form._id || !isDraft) return;
    if (!window.confirm(`Delete draft ${form.transaction_no}?`)) return;
    setSaving(true);
    try {
      await axios.delete(
        `${url}/api/accounting/manual-journal/${form._id}`,
        {
          data: companyParams,
          headers: getAuthHeaders(),
        }
      );
      setForm(createEmptyForm("", companyCurrency));
      setPartyType("");
      setPartyId("");
      await refreshList();
      showMessage("success", "Draft deleted");
    } catch (error) {
      showMessage("error", unwrapError(error, "Failed to delete draft"));
    } finally {
      setSaving(false);
    }
  };

  // ---------- Open Entry ----------
  const openEntry = async (entryId) => {
    setSaving(true);
    try {
      const response = await axios.get(
        `${url}/api/accounting/manual-journal/${entryId}`,
        {
          params: companyParams,
          headers: getAuthHeaders(),
        }
      );
      const transaction = response.data?.data;
      if (transaction) {
        setForm({
          _id: transaction._id,
          version: transaction.version || 1,
          transaction_no: transaction.transaction_no || "",
          reference_no: transaction.reference_no || "",
          journal_date: String(transaction.transaction_date || new Date()).slice(0, 10),
          description: transaction.description || "",
          currency_code: transaction.currency_code || companyCurrency,
          status: transaction.status || "draft",
          lines: (transaction.lines || []).map(line => ({
            id: line._id || generateLineId(),
            account_id: typeof line.account_id === "object" ? line.account_id?._id || "" : line.account_id || "",
            customer_id: line.customer_id || "",
            supplier_id: line.supplier_id || "",
            debit: Number(line.debit || 0) > 0 ? String(line.debit) : "",
            credit: Number(line.credit || 0) > 0 ? String(line.credit) : "",
            description: line.description || "",
          })),
        });
        // Restore party selection
        const party = transaction.transaction_party || "";
        setPartyType(party);
        if (party === "customer") {
          setPartyId(transaction.customer_id || "");
        } else if (party === "supplier") {
          setPartyId(transaction.supplier_id || "");
        } else if (party === "owner") {
          setPartyId(transaction.owner_id || "");
        } else {
          setPartyId("");
        }
        window.scrollTo({ top: 0, behavior: "smooth" });
      }
    } catch (error) {
      showMessage("error", unwrapError(error, "Failed to open entry"));
    } finally {
      setSaving(false);
    }
  };

  // ---------- New Entry ----------
  const newEntry = () => {
    setForm(createEmptyForm("", companyCurrency));
    setPartyType("");
    setPartyId("");
    setMessage(null);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // ---------- Render ----------
  if (loading) {
    return <div className="mj-loading">Loading...</div>;
  }

  return (
    <div className="mj-page">
      {/* Header */}
      <header className="mj-page-header">
        <div>
          <p className="mj-eyebrow">ACCOUNTING</p>
          <h1>Manual Journal Entry</h1>
          <p>
            Create manual journal entries. Drafts can be edited; posted entries are locked.
            Use for adjustments, corrections, and transfers not handled by other modules.
          </p>
        </div>
        <div className="mj-header-actions">
          <button className="mj-button mj-button-secondary" onClick={newEntry} disabled={saving}>
            <FiPlus /> New
          </button>
          <button
            className="mj-button mj-button-secondary"
            onClick={saveDraft}
            disabled={saving || !isDraft}
          >
            <FiSave /> Save Draft
          </button>
          <button
            className="mj-button mj-button-primary"
            onClick={postJournal}
            disabled={saving || !isDraft || !isBalanced}
          >
            <FiSend /> Post Journal
          </button>
          {isDraft && form._id ? (
            <button className="mj-button mj-button-danger" onClick={deleteDraft} disabled={saving}>
              <FiTrash2 /> Delete
            </button>
          ) : null}
        </div>
      </header>

      {/* Alerts */}
      {message ? (
        <div className={`mj-alert mj-alert-${message.type}`}>
          {message.type === "success" ? <FiCheckCircle /> : <FiAlertCircle />}
          <span>{message.text}</span>
          <button onClick={() => setMessage(null)} aria-label="Close message">
            <FiX />
          </button>
        </div>
      ) : null}

      {/* Summary */}
      <div className="mj-summary-grid">
        <article className="mj-summary-card">
          <span>Total entries</span>
          <strong>{summary.total}</strong>
          <small>All manual journal vouchers</small>
        </article>
        <article className="mj-summary-card">
          <span>Draft</span>
          <strong>{summary.draft}</strong>
          <small>Editable and not posted</small>
        </article>
        <article className="mj-summary-card">
          <span>Posted</span>
          <strong>{summary.posted}</strong>
          <small>Included in account balances</small>
        </article>
        <article className="mj-summary-card">
          <span>Current difference</span>
          <strong className={isBalanced ? "mj-positive" : difference ? "mj-negative" : ""}>
            {formatAmount(Math.abs(difference))}
          </strong>
          <small>{isBalanced ? "Voucher is balanced" : "Debit minus credit"}</small>
        </article>
      </div>

      {/* Entry Form */}
      <div className="mj-entry-card">
        <div className="mj-entry-title-row">
          <div>
            <h2>Journal Entry</h2>
            <span className={`mj-status mj-status-${form.status}`}>
              {statusLabel(form.status)}
            </span>
          </div>
          <strong>{form.transaction_no || "New voucher"}</strong>
        </div>

        <div className="mj-header-form">
          <label>
            <span>Journal Date *</span>
            <input
              type="date"
              value={form.journal_date}
              onChange={(e) => changeHeader("journal_date", e.target.value)}
              disabled={!isDraft || saving}
            />
          </label>
          <label>
            <span>Reference</span>
            <input
              value={form.reference_no}
              onChange={(e) => changeHeader("reference_no", e.target.value.toUpperCase())}
              disabled={!isDraft || saving}
              placeholder="Auto if empty"
            />
            <small>MJE- will be added automatically</small>
          </label>
          <label>
            <span>Currency</span>
            <input
              value={form.currency_code}
              maxLength={3}
              onChange={(e) => changeHeader("currency_code", e.target.value.toUpperCase())}
              disabled={!isDraft || saving}
            />
          </label>
        </div>

        {/* Party Selection */}
        <div className="mj-party-section">
          <div className="mj-party-row">
            <label>
              <span>Part Type</span>
              <select
                value={partyType}
                onChange={(e) => {
                  setPartyType(e.target.value);
                  setPartyId("");
                }}
                disabled={!isDraft || saving}
              >
                <option value="">-- No Party --</option>
                <option value="customer">Customer</option>
                <option value="supplier">Supplier</option>
                <option value="owner">Owner</option>
              </select>
            </label>

            {partyType === "owner" && (
              <label>
                <span>Select Owner</span>
                <select
                  value={partyId}
                  onChange={(e) => setPartyId(e.target.value)}
                  disabled={!isDraft || saving}
                >
                  <option value="">-- Select Owner --</option>
                  {ownerAccounts.map(acc => (
                    <option key={acc._id} value={acc._id}>{acc.label}</option>
                  ))}
                </select>
                {ownerAccounts.length === 0 && <small style={{ color: '#d97706' }}>No owner subaccounts found.</small>}
              </label>
            )}

            {partyType === "customer" && (
              <label>
                <span>Customer</span>
                <select
                  value={partyId}
                  onChange={(e) => setPartyId(e.target.value)}
                  disabled={!isDraft || saving}
                >
                  <option value="">Select Customer</option>
                  {parties.customers.map(p => (
                    <option key={p._id} value={p._id}>{p.name}</option>
                  ))}
                </select>
              </label>
            )}

            {partyType === "supplier" && (
              <label>
                <span>Supplier</span>
                <select
                  value={partyId}
                  onChange={(e) => setPartyId(e.target.value)}
                  disabled={!isDraft || saving}
                >
                  <option value="">Select Supplier</option>
                  {parties.suppliers.map(p => (
                    <option key={p._id} value={p._id}>{p.name}</option>
                  ))}
                </select>
              </label>
            )}
          </div>

          <label className="mj-description-field">
            <span>Description *</span>
            <input
              value={form.description}
              onChange={(e) => changeHeader("description", e.target.value)}
              disabled={!isDraft || saving}
              placeholder="Enter journal description"
            />
          </label>
        </div>

        {/* Lines Table */}
        <div className="mj-table-wrap">
          <table className="mj-entry-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Account *</th>
                <th>Line Description</th>
                <th>Debit (DR)</th>
                <th>Credit (CR)</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {form.lines.map((line, index) => {
                const selectedAccounts = form.lines
                  .filter((l) => l.id !== line.id)
                  .map((l) => l.account_id)
                  .filter(Boolean);

                return (
                  <tr key={line.id}>
                    <td className="mj-line-number">{index + 1}</td>
                    <td>
                      <AccountDropdown
                        accountsTree={accountsTree}
                        value={line.account_id}
                        selectedAccounts={selectedAccounts}
                        disabled={!isDraft || saving}
                        onChange={(accountId) => changeLine(line.id, "account_id", accountId)}
                      />
                    </td>
                    <td>
                      <input
                        type="text"
                        value={line.description}
                        onChange={(e) => changeLine(line.id, "description", e.target.value)}
                        disabled={!isDraft || saving}
                        placeholder="Optional"
                      />
                    </td>
                    <td>
                      <input
                        className="mj-money-input"
                        type="number"
                        min="0"
                        step="0.01"
                        value={line.debit}
                        onChange={(e) => changeLine(line.id, "debit", e.target.value)}
                        disabled={!isDraft || saving || (Number(line.credit) > 0)}
                        placeholder="0.00"
                      />
                    </td>
                    <td>
                      <input
                        className="mj-money-input"
                        type="number"
                        min="0"
                        step="0.01"
                        value={line.credit}
                        onChange={(e) => changeLine(line.id, "credit", e.target.value)}
                        disabled={!isDraft || saving || (Number(line.debit) > 0)}
                        placeholder="0.00"
                      />
                    </td>
                    <td>
                      <button
                        className="mj-icon-button mj-icon-danger"
                        onClick={() => removeLine(line.id)}
                        disabled={!isDraft || saving || form.lines.length <= 2}
                        type="button"
                      >
                        <FiTrash2 />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot>
              <tr>
                <td colSpan="3">
                  <button className="mj-add-line" type="button" onClick={addLine} disabled={!isDraft || saving}>
                    <FiPlus /> Add Line
                  </button>
                </td>
                <td>{formatAmount(totals.debit)}</td>
                <td>{formatAmount(totals.credit)}</td>
                <td>
                  <span className={isBalanced ? "mj-balance-ok" : "mj-balance-warning"}>
                    {isBalanced ? <FiCheckCircle /> : <FiAlertCircle />}
                    {isBalanced
                      ? "Balanced"
                      : `Difference ${formatAmount(Math.abs(difference))}`}
                  </span>
                </td>
              </tr>
            </tfoot>
          </table>
        </div>

        <div className="mj-note">
          <FiAlertCircle />
          <div>
            <strong>Posting rule</strong>
            <p>
              Each line must have either a debit or credit. Total debit must equal total credit.
              For AR/AP accounts, select a customer/supplier subaccount.
            </p>
          </div>
        </div>
      </div>

      {/* History Section */}
      <div className="mj-list-card">
        <div className="mj-list-toolbar">
          <div>
            <h2>Manual Journal History</h2>
            <p>Open a draft to continue editing or review a posted voucher.</p>
          </div>
          <div className="mj-list-filters">
            <label className="mj-search-box">
              <FiSearch />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search voucher, reference or description"
              />
            </label>
            <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
              <option value="all">All statuses</option>
              <option value="draft">Draft</option>
              <option value="posted">Posted</option>
              <option value="reversed">Reversed</option>
            </select>
            <button className="mj-icon-button" onClick={refreshList} title="Refresh">
              <FiRefreshCw />
            </button>
          </div>
        </div>

        <div className="mj-table-wrap">
          <table className="mj-history-table">
            <thead>
              <tr>
                <th>Voucher</th>
                <th>Date</th>
                <th>Description</th>
                <th>Debit</th>
                <th>Credit</th>
                <th>Status</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {entries.length ? (
                entries.map((entry) => (
                  <tr key={entry._id}>
                    <td>
                      <strong>{entry.transaction_no}</strong>
                      <small>{entry.reference_no || "—"}</small>
                    </td>
                    <td>{String(entry.transaction_date).slice(0, 10)}</td>
                    <td>{entry.description}</td>
                    <td>{formatAmount(entry.total_debit || 0)}</td>
                    <td>{formatAmount(entry.total_credit || 0)}</td>
                    <td>
                      <span className={`mj-status mj-status-${entry.status}`}>
                        {statusLabel(entry.status)}
                      </span>
                    </td>
                    <td>
                      <button className="mj-open-button" onClick={() => openEntry(entry._id)}>
                        <FiFileText /> Open
                      </button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="7" className="mj-empty-state">
                    <FiClock />
                    <strong>No manual journal entries found</strong>
                    <span>Create a new draft to begin.</span>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default ManualJournalEntryForm;